import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/cn";

// ════════════════════════════════════════════════════════════════════════
// Badge — small status pill.
// Use for: "For you", "Good rep", "Flag · review", team / division tags.
// ════════════════════════════════════════════════════════════════════════

export const badgeVariants = cva(
  [
    "inline-flex items-center gap-1.5 rounded-full",
    "font-mono text-[10px] font-semibold uppercase tracking-mono",
    "px-2.5 py-1 whitespace-nowrap",
    "transition-colors duration-fast ease-out",
  ].join(" "),
  {
    variants: {
      variant: {
        // Hero notify pill — "For you", new feedback delivered.
        accent: "bg-accent text-accent-ink",
        // Positive rep — a good moment.
        good: "bg-good text-paper",
        // Review flag — coach wants this clip back.
        flag: "bg-flag text-navy",
        // Neutral team / division marker.
        team: "bg-ink text-ink-inverse",
        // Soft / muted — secondary metadata.
        muted: "bg-field text-ink-secondary",
        // Outline — when on top of a colored surface.
        outline: "border border-rule-strong text-ink",
      },
    },
    defaultVariants: { variant: "team" },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

export const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant, ...props }, ref) => (
    <span
      ref={ref}
      className={cn(badgeVariants({ variant, className }))}
      {...props}
    />
  ),
);
Badge.displayName = "Badge";
